import {
  CheckCircle,
  Clock,
  CreditCard,
  X,
  User,
  Mail,
  Phone,
  Loader2,
  ChevronLeft,
  Tag,
  XCircle,
  ChevronRight,
  BadgePercent,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { paymentManager } from "../api";

const API_BASE = "https://api.vantagepeoplehq.com/api";

export default function TierConfirmation({
  service = "cv-review",
  delivery = {
    delivery: "Early Career",
    timeline: "3-5 days",
    price: "₦8,000",
  },
  tier = { name: "Professional", price: "₦8,000" },
  onBack = () => alert("Going back"),
}) {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [formErrors, setFormErrors] = useState({});

  // ── Discount state ──────────────────────────────────────────────────────────
  const [discountInput, setDiscountInput] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  // appliedDiscount shape: { code, type, value, discountAmount }
  const discountInputRef = useRef(null);

  // ── Price helpers ───────────────────────────────────────────────────────────
  const getBasePrice = () => (tier ? tier.price : delivery.price);

  const getBasePriceValue = () =>
    parseFloat(getBasePrice().replace(/[^0-9.]/g, ""));

  const getDiscountAmount = () => appliedDiscount?.discountAmount ?? 0;

  const getFinalPriceValue = () =>
    Math.max(0, getBasePriceValue() - getDiscountAmount());

  const formatNaira = (num) =>
    "₦" + num.toLocaleString("en-NG", { minimumFractionDigits: 0 });

  const getFinalPrice = () => formatNaira(getFinalPriceValue());

  const getServiceName = () =>
    service === "cv-review" ? "CV Review & Optimization" : "New CV Creation";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ── Apply discount  POST /api/discounts/validate ────────────────────────────
  const applyDiscount = async () => {
    const code = discountInput.trim().toUpperCase();
    if (!code) {
      setDiscountError("Please enter a discount code.");
      return;
    }

    setDiscountLoading(true);
    setDiscountError("");
    setAppliedDiscount(null);

    try {
      const res = await fetch(`${API_BASE}/discounts/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, orderAmount: getBasePriceValue() }),
      });

      const data = await res.json();

      if (!res.ok) {
        setDiscountError(data.message || "Invalid or expired discount code.");
        return;
      }

      // data shape: { success, message, discountAmount, data: discountRecord }
      setAppliedDiscount({
        code,
        type: data.data.type,
        value: data.data.value,
        discountAmount: data.discountAmount,
      });
      setDiscountInput("");
    } catch (err) {
      setDiscountError("Could not verify code. Please try again.");
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountError("");
    setDiscountInput("");
    setTimeout(() => discountInputRef.current?.focus(), 50);
  };

  // ── Form validation ─────────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errors.email = "Invalid email format";
    if (!formData.phone.trim()) errors.phone = "Phone number is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleProceedToPayment = () => {
    setShowModal(true);
    setError("");
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setFormErrors({});
    setError("");
  };

  // ── Payment status polling ──────────────────────────────────────────────────
  const checkPaymentStatus = async (reference) => {
    try {
      const res = await fetch(
        `https://api.vantagepeoplehq.com/api/payment/verify/${reference}`,
      );
      const data = await res.json();
      if (
        data.success &&
        (data.data.status === "success" || data.data.status === "failed")
      ) {
        window.location.href = `https://api.vantagepeoplehq.com/payment-successful?trxref=${reference}&reference=${reference}`;
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  // ── Submit payment ──────────────────────────────────────────────────────────
  const handleSubmitPayment = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    const paymentWindow = window.open("", "_blank");

    const checkIfClosed = setInterval(async () => {
      if (!paymentWindow || paymentWindow.closed) {
        await paymentManager.trackAbandonmentOnExit();
        clearInterval(checkIfClosed);
        setLoading(false);
      }
    }, 1000);

    try {
      const paymentData = {
        email: formData.email,
        name: formData.name,
        phone: formData.phone,
        amount: getFinalPriceValue(), // ← discounted amount
        metadata: {
          service: getServiceName(),
          delivery: `${delivery.delivery} - ${delivery.timeline}`,
          tier: tier ? `${tier.name} Tier` : null,
          discountCode: appliedDiscount?.code ?? null,
          discountAmount: appliedDiscount?.discountAmount ?? 0,
          originalAmount: getBasePriceValue(),
        },
      };

      const paymentResponse = await paymentManager.initiate(paymentData);

      if (paymentWindow)
        paymentWindow.location.href = paymentResponse.authorization_url;
      else window.location.href = paymentResponse.authorization_url;

      setTimeout(() => {
        checkPaymentStatus(paymentResponse.reference);
      }, 10000);

      const checkInterval = setInterval(async () => {
        const ok = await checkPaymentStatus(paymentResponse.reference);
        if (ok) clearInterval(checkInterval);
      }, 5000);

      setTimeout(() => {
        clearInterval(checkInterval);
        setLoading(false);
      }, 300000);
    } catch (err) {
      if (paymentWindow) paymentWindow.close();
      setError(
        err.message || "Failed to initialize payment. Please try again.",
      );
      setLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full pt-5">
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex font-semibold items-center gap-2 text-blue-600 hover:text-blue-700 mb-8 transition-colors"
        >
          <ChevronLeft size={24} />
          Back to Services
        </button>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-8 text-white text-center">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-2">
              Confirm Your Selection
            </h2>
            <p className="text-blue-100">
              Review your choices before proceeding to payment
            </p>
          </div>

          <div className="p-8 md:p-12">
            {/* Order summary cards */}
            <div className="space-y-4 mb-8">
              {/* Service */}
              <div className="flex items-start gap-4 p-5 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl">
                <div className="w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5 uppercase tracking-wide font-medium">
                    Service
                  </p>
                  <p className="text-lg font-bold text-gray-800">
                    {getServiceName()}
                  </p>
                </div>
              </div>

              {/* Delivery */}
              <div className="flex items-start gap-4 p-5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl">
                <div className="w-11 h-11 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5 uppercase tracking-wide font-medium">
                    Delivery
                  </p>
                  <p className="text-lg font-bold text-gray-800">
                    {delivery.delivery} — {delivery.timeline}
                  </p>
                </div>
              </div>

              {/* Tier */}
              {tier && (
                <div className="flex items-start gap-4 p-5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl">
                  <div className="w-11 h-11 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5 uppercase tracking-wide font-medium">
                      Tier
                    </p>
                    <p className="text-lg font-bold text-gray-800">
                      {tier.name} Tier
                    </p>
                  </div>
                </div>
              )}

              {/* ── Discount Code Entry ── */}
              <div className="border border-dashed border-blue-200 bg-blue-50/40 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <BadgePercent className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-semibold text-blue-700">
                    Have a discount code?
                  </span>
                </div>

                {/* Applied state */}
                {appliedDiscount ? (
                  <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Tag className="w-4 h-4 text-emerald-600" />
                      <div>
                        <span className="font-bold text-emerald-700 font-mono tracking-wide text-sm">
                          {appliedDiscount.code}
                        </span>
                        <p className="text-xs text-emerald-600">
                          {appliedDiscount.type === "percentage"
                            ? `${appliedDiscount.value}% off`
                            : `${formatNaira(appliedDiscount.value)} off`}{" "}
                          · saving {formatNaira(appliedDiscount.discountAmount)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={removeDiscount}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove discount"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  /* Input row */
                  <div className="flex gap-2">
                    <input
                      ref={discountInputRef}
                      type="text"
                      value={discountInput}
                      onChange={(e) => {
                        setDiscountInput(e.target.value.toUpperCase());
                        setDiscountError("");
                      }}
                      onKeyDown={(e) => e.key === "Enter" && applyDiscount()}
                      placeholder="e.g. SUMMER25"
                      className="flex-1 px-4 py-2.5 border-2 border-gray-200 focus:border-blue-400 rounded-xl text-sm font-mono tracking-wide outline-none bg-white transition-colors placeholder:font-sans placeholder:tracking-normal"
                      disabled={discountLoading}
                    />
                    <button
                      onClick={applyDiscount}
                      disabled={discountLoading || !discountInput.trim()}
                      className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap"
                    >
                      {discountLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <span>Apply</span>
                          <ChevronRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Discount error */}
                {discountError && (
                  <p className="flex items-center gap-1.5 text-red-500 text-xs mt-2 font-medium">
                    <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {discountError}
                  </p>
                )}
              </div>

              {/* ── Price breakdown ── */}
              <div className="border-t-2 border-gray-100 pt-5 space-y-2">
                {/* Original price row — only show when discount applied */}
                {appliedDiscount && (
                  <div className="flex justify-between items-center text-gray-500">
                    <span className="text-sm">Original Price</span>
                    <span className="text-sm line-through">
                      {getBasePrice()}
                    </span>
                  </div>
                )}

                {/* Discount row */}
                {appliedDiscount && (
                  <div className="flex justify-between items-center text-emerald-600">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5" />
                      Discount ({appliedDiscount.code})
                    </span>
                    <span className="text-sm font-semibold">
                      − {formatNaira(appliedDiscount.discountAmount)}
                    </span>
                  </div>
                )}

                {/* Total */}
                <div className="flex justify-between items-center pt-1">
                  <p className="text-lg text-gray-600 font-medium">
                    Total Amount
                  </p>
                  <div className="text-right">
                    <p className="text-4xl font-bold text-gray-900">
                      {getFinalPrice()}
                    </p>
                    {appliedDiscount && (
                      <p className="text-xs text-emerald-600 font-medium mt-0.5">
                        You save {formatNaira(appliedDiscount.discountAmount)}{" "}
                        🎉
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleProceedToPayment}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-2xl text-lg font-semibold hover:shadow-2xl transition-all hover:scale-[1.02] flex items-center justify-center gap-3"
            >
              <CreditCard className="w-6 h-6" />
              Proceed to Payment
            </button>
            <p className="text-center text-sm text-gray-400 mt-4">
              You'll be redirected to Paystack for secure payment
            </p>
          </div>
        </div>
      </div>

      {/* ── Payment Details Modal ─────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto animate-fadeIn">
          <div className="bg-white mt-20 mb-8 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
            {/* Modal header */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-6 text-white flex items-center justify-between">
              <h3 className="text-2xl font-bold">Payment Details</h3>
              <button
                onClick={handleCloseModal}
                disabled={loading}
                className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-all disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-500 mb-6 text-center text-sm">
                Please provide your details to proceed with payment
              </p>

              {/* Error */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Name */}
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    disabled={loading}
                    className={`w-full pl-11 pr-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${formErrors.name ? "border-red-400" : "border-gray-300"}`}
                  />
                </div>
                {formErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                )}
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    disabled={loading}
                    className={`w-full pl-11 pr-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${formErrors.email ? "border-red-400" : "border-gray-300"}`}
                  />
                </div>
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    {formErrors.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div className="mb-5">
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+234 801 234 5678"
                    disabled={loading}
                    className={`w-full pl-11 pr-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${formErrors.phone ? "border-red-400" : "border-gray-300"}`}
                  />
                </div>
                {formErrors.phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {formErrors.phone}
                  </p>
                )}
              </div>

              {/* Amount summary in modal */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl space-y-2">
                {appliedDiscount && (
                  <>
                    <div className="flex justify-between items-center text-gray-500 text-sm">
                      <span>Original</span>
                      <span className="line-through">{getBasePrice()}</span>
                    </div>
                    <div className="flex justify-between items-center text-emerald-600 text-sm">
                      <span className="flex items-center gap-1">
                        <Tag className="w-3.5 h-3.5" /> {appliedDiscount.code}
                      </span>
                      <span className="font-semibold">
                        − {formatNaira(appliedDiscount.discountAmount)}
                      </span>
                    </div>
                    <div className="border-t border-blue-100 pt-2" />
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium text-sm">
                    Amount to Pay
                  </span>
                  <span className="text-2xl font-bold text-gray-800">
                    {getFinalPrice()}
                  </span>
                </div>
              </div>

              {/* Pay button */}
              <button
                onClick={handleSubmitPayment}
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-xl text-lg font-semibold hover:shadow-xl transition-all hover:scale-[1.02] flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" /> Pay {getFinalPrice()}
                  </>
                )}
              </button>

              <p className="text-center text-xs text-gray-400 mt-4">
                Secured by Paystack · Your payment information is encrypted
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
      `}</style>
    </div>
  );
}
